import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { products as initialProducts, type Product } from '../data/products'

type Category = { name: string; description: string }
type ProductForm = Omit<Product, 'id'>

const defaultCategories: Category[] = [
  { name: 'Knee Joint', description: '膝关节与步态支撑' },
  { name: 'Foot', description: '动态假脚与足部组件' },
  { name: 'Myoelectric Hand', description: '肌电手与上肢产品' },
  { name: 'Silicone Prosthesis', description: '硅胶假肢与美容覆盖' },
  { name: 'Liner', description: '硅胶内衬与悬吊' },
  { name: 'Adapter', description: '连接器与对线组件' },
  { name: 'Material', description: '制作假肢所需材料' },
  { name: 'Socks', description: '穿戴辅助与日常耗材' },
  { name: 'Shuttle Lock', description: '锁具与悬吊系统' }
]

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const blankProduct: ProductForm = {
  name: '',
  code: '',
  category: 'Knee Joint',
  slug: '',
  categorySlug: 'knee-joint',
  image: '/images/products/hydraulic-knee-joint-a3-hkj.webp',
  alt: '',
  summary: '',
  details: ['']
}

// 当前为前端原型门禁。正式部署时应替换为服务端登录鉴权。
const ADMIN_PASSWORD = 'angeltech2026'

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem('angeltech-admin-auth') === 'true' && !!sessionStorage.getItem('angeltech-admin-password'))
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [products, setProducts] = useState<Product[]>(initialProducts)
  const [categories, setCategories] = useState<Category[]>(defaultCategories)
  const [activeCategory, setActiveCategory] = useState('All products')
  const [query, setQuery] = useState('')
  const [productModal, setProductModal] = useState<ProductForm & { id?: number } | null>(null)
  const [categoryModal, setCategoryModal] = useState<{ mode: 'add' | 'edit'; oldName?: string; name: string; description: string } | null>(null)
  const [notice, setNotice] = useState('')
  const syncRequest = useRef<Promise<void>>(Promise.resolve())

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (loginPassword === ADMIN_PASSWORD) {
      sessionStorage.setItem('angeltech-admin-auth', 'true')
      sessionStorage.setItem('angeltech-admin-password', loginPassword)
      setLoginError('')
      // 重新初始化一次后台，确保登录后的数据和页面状态完整加载。
      window.location.reload()
      return
    }
    setLoginError('密码不正确，请重试')
    setLoginPassword('')
  }

  if (!isAuthenticated) return <div className="admin-login-page"><div className="admin-login-card"><img src="/images/brand-mark.svg" alt="Angel Technology" /><span className="login-kicker">ANGEL TECHNOLOGY</span><h1>产品管理后台</h1><p>请输入管理员密码继续</p><form onSubmit={handleLogin}><label>管理员密码<input autoFocus type="password" value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} placeholder="请输入密码" /></label>{loginError && <div className="login-error">{loginError}</div>}<button className="primary-btn" type="submit">进入后台　→</button></form><small>内部内容管理工作台</small></div></div>

  useEffect(() => {
    fetch('/api/content').then((response) => response.ok ? response.json() : Promise.reject()).then((content: { products?: Product[]; categories?: Category[] }) => {
      if (!Array.isArray(content.products) || !Array.isArray(content.categories)) return
      setProducts(content.products)
      setCategories(content.categories)
    }).catch(() => undefined)
  }, [])

  const syncContent = (nextProducts: Product[], nextCategories: Category[]) => {
    const password = sessionStorage.getItem('angeltech-admin-password') || ''
    syncRequest.current = syncRequest.current.then(() => fetch('/api/content', { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Admin-Password': password }, body: JSON.stringify({ products: nextProducts, categories: nextCategories }) })
      .then((response) => { if (!response.ok) throw new Error('sync failed'); flash('已保存到服务器') })
      .catch(() => flash('服务器保存失败，暂存于当前浏览器')))
  }
  const persistProducts = (next: Product[]) => { setProducts(next); localStorage.setItem('angeltech-admin-products', JSON.stringify(next)); syncContent(next, categories) }
  const persistCategories = (next: Category[]) => { setCategories(next); localStorage.setItem('angeltech-admin-categories', JSON.stringify(next)); syncContent(products, next) }
  const persistAll = (nextProducts: Product[], nextCategories: Category[]) => {
    setProducts(nextProducts)
    setCategories(nextCategories)
    localStorage.setItem('angeltech-admin-products', JSON.stringify(nextProducts))
    localStorage.setItem('angeltech-admin-categories', JSON.stringify(nextCategories))
    syncContent(nextProducts, nextCategories)
  }
  const flash = (message: string) => { setNotice(message); window.setTimeout(() => setNotice(''), 2400) }

  const visibleProducts = useMemo(() => products.filter((product) => {
    const matchesCategory = activeCategory === 'All products' || product.category === activeCategory
    const searchable = `${product.name} ${product.code} ${product.summary}`.toLowerCase()
    return matchesCategory && searchable.includes(query.toLowerCase())
  }), [products, activeCategory, query])

  const saveProduct = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!productModal?.name.trim() || !productModal.code.trim()) return
    const filled: ProductForm = {
      ...productModal,
      slug: productModal.slug.trim() || slugify(`${productModal.name} ${productModal.code}`),
      categorySlug: slugify(productModal.category),
      alt: productModal.alt.trim() || `${productModal.code} ${productModal.name} prosthetic component`,
      details: productModal.details.filter(Boolean)
    }
    const next = productModal.id
      ? products.map((item) => (item.id === productModal.id ? { ...filled, id: item.id } : item))
      : [...products, { ...filled, id: Math.max(...products.map((item) => item.id), 0) + 1 }]
    persistProducts(next as Product[])
    setProductModal(null)
    flash(productModal.id ? '产品信息已更新' : '产品已添加')
  }

  const saveCategory = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!categoryModal?.name.trim()) return
    if (categoryModal.mode === 'add') {
      persistCategories([...categories, { name: categoryModal.name.trim(), description: categoryModal.description.trim() }])
      setActiveCategory(categoryModal.name.trim())
      flash('分类已添加')
    } else if (categoryModal.oldName) {
      const nextName = categoryModal.name.trim()
      const nextCategories = categories.map((item) => item.name === categoryModal.oldName ? { name: nextName, description: categoryModal.description.trim() } : item)
      const nextProducts = products.map((item) => item.category === categoryModal.oldName ? { ...item, category: nextName } : item)
      persistAll(nextProducts, nextCategories)
      setActiveCategory(nextName)
      flash('分类及关联产品已更新')
    }
    setCategoryModal(null)
  }

  const deleteCategory = (category: Category) => {
    const count = products.filter((product) => product.category === category.name).length
    if (!window.confirm(`删除“${category.name}”？${count ? `该分类下的 ${count} 个产品也会被删除。` : ''}`)) return
    persistAll(products.filter((item) => item.category !== category.name), categories.filter((item) => item.name !== category.name))
    setActiveCategory('All products')
    flash('分类已删除')
  }

  const deleteProduct = (product: Product) => {
    if (!window.confirm(`确定删除「${product.name}」吗？`)) return
    persistProducts(products.filter((item) => item.id !== product.id))
    flash('产品已删除')
  }

  const handleImageUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      flash('请选择图片文件')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const image = new Image()
      image.onload = () => {
        const scale = Math.min(1, 1200 / Math.max(image.width, image.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(image.width * scale)
        canvas.height = Math.round(image.height * scale)
        canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height)
        setProductModal((current) => current ? { ...current, image: canvas.toDataURL('image/jpeg', 0.84) } : current)
      }
      image.src = String(reader.result)
    }
    reader.readAsDataURL(file)
  }

  return <div className="admin-shell">
    <aside className="admin-sidebar">
      <div className="admin-brand">
        <img src="/images/brand-mark.svg" alt="Angel Technology" />
        <div><strong>Angel Technology</strong><span>CONTENT WORKSPACE</span></div>
      </div>
      <div className="admin-side-label">内容管理</div>
      <button className={`admin-nav-item ${activeCategory === 'All products' ? 'active' : ''}`} onClick={() => setActiveCategory('All products')}><span>▦</span>全部产品 <b>{products.length}</b></button>
      <div className="admin-side-label category-label">产品分类 <button onClick={() => setCategoryModal({ mode: 'add', name: '', description: '' })}>＋</button></div>
      <div className="category-list">{categories.map((category) => <div key={category.name} className={`admin-category-row ${activeCategory === category.name ? 'active' : ''}`}>
        <button onClick={() => setActiveCategory(category.name)}><span className="category-dot" />{category.name}<b>{products.filter((item) => item.category === category.name).length}</b></button>
        <div className="category-actions"><button aria-label={`编辑 ${category.name}`} onClick={() => setCategoryModal({ mode: 'edit', oldName: category.name, name: category.name, description: category.description })}>···</button></div>
      </div>)}</div>
      <div className="admin-sidebar-bottom"><button className="back-site" onClick={() => { window.location.href = '/' }}>↗ <span>返回官网</span></button><div className="admin-user"><div className="avatar">AT</div><div><strong>Admin</strong><span>内容管理员</span></div><button aria-label="退出登录" onClick={() => { sessionStorage.removeItem('angeltech-admin-auth'); setIsAuthenticated(false) }}>退出</button></div></div>
    </aside>

    <main className="admin-main">
      <header className="admin-topbar"><div><div className="breadcrumb">工作台 <span>/</span> 产品管理</div><h1>产品管理</h1><p>管理官网展示的产品和产品分类</p></div><button className="primary-btn" onClick={() => setProductModal({ ...blankProduct, category: activeCategory === 'All products' ? categories[0]?.name || '' : activeCategory })}>＋ 新增产品</button></header>
      <section className="admin-content">
        <div className="stats-row"><div><span className="stat-icon teal">▦</span><div><span>全部产品</span><strong>{products.length}</strong></div></div><div><span className="stat-icon blue">◫</span><div><span>产品分类</span><strong>{categories.length}</strong></div></div><div><span className="stat-icon amber">↗</span><div><span>最近更新</span><strong>今天</strong></div></div></div>
        <div className="table-card"><div className="table-toolbar"><div><h2>{activeCategory}</h2><span>共 {visibleProducts.length} 个产品</span></div><div className="toolbar-controls"><label className="search-box"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索产品名称或编号" /></label><button className="filter-btn">☷ 筛选</button></div></div>
          <div className="product-table-wrap"><table className="product-table"><thead><tr><th>产品</th><th>产品编号</th><th>所属分类</th><th>简介</th><th>操作</th></tr></thead><tbody>{visibleProducts.map((product) => <tr key={product.id}><td><div className="product-cell"><img src={product.image} alt="" /><div><strong>{product.name}</strong><span>产品 #{String(product.id).padStart(3, '0')}</span></div></div></td><td><code>{product.code}</code></td><td><span className="tag">{product.category}</span></td><td><p className="summary-cell">{product.summary}</p></td><td><div className="row-actions"><button onClick={() => setProductModal({ ...product })}>编辑</button><button className="delete-action" onClick={() => deleteProduct(product)}>删除</button></div></td></tr>)}</tbody></table>{visibleProducts.length === 0 && <div className="empty-state"><span>⌕</span><strong>没有找到相关产品</strong><p>试试更换搜索关键词或分类</p></div>}</div>
          <div className="table-footer"><span>显示 {visibleProducts.length} 条记录</span><div><button disabled>‹</button><button className="current">1</button><button disabled>›</button></div></div>
        </div>
      </section>
    </main>

    {notice && <div className="admin-toast">✓ {notice}</div>}
    {productModal && <div className="modal-backdrop" onMouseDown={(event) => event.currentTarget === event.target && setProductModal(null)}><form className="admin-modal" onSubmit={saveProduct}><div className="modal-head"><div><span className="modal-kicker">PRODUCT</span><h2>{productModal.id ? '编辑产品' : '新增产品'}</h2></div><button type="button" onClick={() => setProductModal(null)}>×</button></div><div className="form-grid"><label>产品名称<input required value={productModal.name} onChange={(e) => setProductModal({ ...productModal, name: e.target.value })} placeholder="例如：Hydraulic Knee Joint" /></label><label>产品编号<input required value={productModal.code} onChange={(e) => setProductModal({ ...productModal, code: e.target.value })} placeholder="例如：A3-HKJ" /></label><label>所属分类<select value={productModal.category} onChange={(e) => setProductModal({ ...productModal, category: e.target.value })}>{categories.map((category) => <option key={category.name}>{category.name}</option>)}</select></label><div className="image-field"><span>产品图片</span><div className="image-upload-row"><div className="image-preview"><img src={productModal.image} alt="产品预览" /></div><div><label className="upload-btn">上传图片<input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleImageUpload(file); e.currentTarget.value = '' }} /></label><small>支持 JPG、PNG、WEBP</small></div></div><input className="image-url-input" value={productModal.image.startsWith('data:') ? '' : productModal.image} onChange={(e) => setProductModal({ ...productModal, image: e.target.value })} placeholder="或输入图片地址" /></div><label className="full-field">产品简介<textarea required rows={3} value={productModal.summary} onChange={(e) => setProductModal({ ...productModal, summary: e.target.value })} placeholder="用一句话介绍产品的核心特点" /></label></div><div className="modal-foot"><button type="button" className="cancel-btn" onClick={() => setProductModal(null)}>取消</button><button className="primary-btn" type="submit">保存产品</button></div></form></div>}
    {categoryModal && <div className="modal-backdrop" onMouseDown={(event) => event.currentTarget === event.target && setCategoryModal(null)}><form className="admin-modal small-modal" onSubmit={saveCategory}><div className="modal-head"><div><span className="modal-kicker">CATEGORY</span><h2>{categoryModal.mode === 'add' ? '新增分类' : '编辑分类'}</h2></div><button type="button" onClick={() => setCategoryModal(null)}>×</button></div><label>分类名称<input required value={categoryModal.name} onChange={(e) => setCategoryModal({ ...categoryModal, name: e.target.value })} /></label><label>分类说明<textarea rows={3} value={categoryModal.description} onChange={(e) => setCategoryModal({ ...categoryModal, description: e.target.value })} /></label><div className="modal-foot category-modal-foot">{categoryModal.mode === 'edit' && <button type="button" className="delete-category-btn" onClick={() => { const item = categories.find((category) => category.name === categoryModal.oldName); if (item) { setCategoryModal(null); deleteCategory(item) } }}>删除分类</button>}<span /><button type="button" className="cancel-btn" onClick={() => setCategoryModal(null)}>取消</button><button className="primary-btn" type="submit">保存分类</button></div></form></div>}
  </div>
}

export default Admin
